"use client";

import React from "react";
import { Lock, ShieldCheck, KeyRound, FileCheck2, CircleCheck as CheckCircle2, ArrowRight } from 'lucide-react';
import { motion, useReducedMotion } from "framer-motion";
import { Link } from "@/components/common/Link";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardHeader, CardTitle, CardContent } from "@/components/ui/card";
const securityPillars = [{
  id: "encryption",
  icon: Lock,
  badge: "Transmission & Storage",
  title: "End-to-end TLS 1.3 & AES-256",
  summary: "All network traffic between your browser and our publishing network is strictly enforced through TLS 1.3. Stored subscriber records and transaction logs are encrypted at rest using AES-256 standards.",
  specs: ["HSTS preloaded headers", "Automated certificate rotation", "Zero plaintext storage"]
}, {
  id: "access",
  icon: KeyRound,
  badge: "Identity Governance",
  title: "Role-based access & multi-factor auth",
  summary: "Internal access to editorial archives, subscriber registries, and download metrics is governed under the principle of least privilege, guarded by mandatory hardware multi-factor authentication.",
  specs: ["Hardware token MFA enforcement", "Ephemeral session management", "Quarterly credential audits"]
}, {
  id: "audits",
  icon: ShieldCheck,
  badge: "Continuous Assurance",
  title: "Vulnerability monitoring & audits",
  summary: "We run weekly automated vulnerability scans and engage independent third-party cybersecurity teams to conduct annual penetration tests across our content delivery network and APIs.",
  specs: ["Automated dependency scanning", "Independent penetration reports", "Strict SLA for patch deployment"]
}, {
  id: "compliance",
  icon: FileCheck2,
  badge: "Regulatory Alignment",
  title: "GDPR, CCPA & privacy standards",
  summary: "We never monetize reader telemetry or sell subscriber lists to third-party data brokers. Data minimization is baked into every research download, newsletter dispatch, and research survey.",
  specs: ["Clear data subject rights", "Explicit opt-in preferences", "No third-party behavioral trackers"]
}];
export default function Security() {
  const shouldReduceMotion = useReducedMotion();
  const containerVariants = {
    hidden: {
      opacity: 0
    },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: shouldReduceMotion ? 0 : 0.1,
        delayChildren: 0.05
      }
    }
  };
  const itemVariants = {
    hidden: {
      opacity: 0,
      y: shouldReduceMotion ? 0 : 16
    },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.45,
        ease: [0.16, 1, 0.3, 1]
      }
    }
  };
  return <section id="security-practices" data-nav="light" className="bg-background text-foreground py-16 sm:py-20 lg:py-24 border-b border-border">
      <div className="max-w-[1280px] mx-auto px-5 sm:px-8 lg:px-12">
        {/* Masthead rule & Section header */}
        <div className="border-t border-border pt-8 mb-12 sm:mb-16">
          <div className="flex flex-col lg:flex-row lg:items-end justify-between gap-6">
            <div className="max-w-2xl">
              <div className="flex items-center gap-2 mb-3">
                <span className="h-1.5 w-1.5 rounded-full bg-primary" />
                <p className="text-xs font-semibold uppercase tracking-wider text-muted-foreground font-sans">
                  Institutional Security & Integrity
                </p>
              </div>
              <h2 className="font-serif text-3xl sm:text-4xl lg:text-5xl font-semibold tracking-tight leading-[1.15] text-foreground">
                How we protect your data
              </h2>
              <p className="mt-4 text-base sm:text-lg text-muted-foreground font-sans leading-relaxed">
                We apply institutional safeguards across every publication download, subscriber profile, and research transaction to ensure complete confidentiality.
              </p>
            </div>

            <div className="flex items-center gap-3">
              <div className="inline-flex items-center gap-2 px-3.5 py-2 rounded-sm bg-muted text-foreground text-xs font-mono border border-border">
                <span className="inline-block size-2 rounded-full bg-chart-4" />
                <span>Security Status: Nominal</span>
              </div>
            </div>
          </div>
        </div>

        {/* 4-Card Security Grid */}
        <motion.div className="grid grid-cols-1 md:grid-cols-2 gap-6 lg:gap-8" variants={containerVariants} initial="hidden" whileInView="visible" viewport={{
        once: true,
        amount: 0.15
      }}>
          {securityPillars.map((pillar, i) => {
          const IconComponent = pillar.icon;
          return <motion.div key={pillar.id} data-index={i} variants={itemVariants} className="h-full">
                <Card className="h-full bg-card border border-border rounded-md shadow-xs transition-[border-color,box-shadow] duration-200 hover:border-primary/40 hover:shadow-md p-6 sm:p-8 flex flex-col justify-between">
                  <div>
                    {/* Card Header Row */}
                    <div className="flex items-start justify-between gap-4 mb-5">
                      <div className="size-11 rounded-sm bg-muted flex items-center justify-center text-primary border border-border shrink-0">
                        <IconComponent className="size-5" />
                      </div>
                      <Badge variant="outline" className="text-[11px] font-mono rounded-sm border-border bg-background text-muted-foreground font-normal py-0.5 px-2">
                        {pillar.badge}
                      </Badge>
                    </div>

                    {/* Card Title & Description */}
                    <CardHeader className="p-0 mb-4 gap-2">
                      <CardTitle className="font-serif text-xl sm:text-2xl font-semibold text-foreground tracking-normal leading-snug">
                        {pillar.title}
                      </CardTitle>
                    </CardHeader>
                    
                    <CardContent className="p-0">
                      <p className="text-sm sm:text-base text-muted-foreground leading-relaxed font-sans mb-6">
                        {pillar.summary}
                      </p>

                      {/* Technical Specs List */}
                      <div className="space-y-2.5 pt-4 border-t border-border">
                        {pillar.specs.map((spec, specIndex) => <div key={specIndex} data-index={specIndex} className="flex items-center gap-2 text-xs sm:text-sm text-foreground/90 font-sans">
                            <CheckCircle2 className="size-3.5 text-chart-4 shrink-0" />
                            <span>{spec}</span>
                          </div>)}
                      </div>
                    </CardContent>
                  </div>
                </Card>
              </motion.div>;
        })}
        </motion.div>

        {/* Supporting Notice / Editorial Footer Strip */}
        <div className="mt-12 sm:mt-16 rounded-md bg-muted p-6 sm:p-8 border border-border flex flex-col sm:flex-row items-start sm:items-center justify-between gap-6">
          <div className="space-y-1 max-w-2xl">
            <h3 className="font-serif text-lg sm:text-xl font-semibold text-foreground">
              Have a security inquiry or vulnerability report?
            </h3>
            <p className="text-sm text-muted-foreground font-sans">
              Our information security team responds directly to privacy requests and vulnerability notices within one business day.
            </p>
          </div>
          <div className="shrink-0 flex items-center gap-3">
            <Button asChild variant="default" className="rounded-sm font-medium">
              <Link to="mailto:info@minfomedia.com" className="inline-flex items-center gap-2">
                <span>Contact security team</span>
                <ArrowRight className="size-4" />
              </Link>
            </Button>
          </div>
        </div>
      </div>
    </section>;
}